import {
  describeWanIpv6,
  type OnuVlan,
  type SubscriberWanWriter,
  type VlanAddressing,
  type VlanMode,
  type WanIpv6,
  type WanMode,
  type WanOwner,
} from '@simpleacs/shared';
import { addressingPatch, firewallModelNote } from './wan-wizard';

/**
 * ONE apply for the subscriber WAN, as BODIES — what the WAN mode wizard sends
 * and what the WAN card's IPv6 rows send, built in the same place so the two
 * surfaces cannot drift apart and the card never grows a second writer.
 *
 * The operator asked for the card to EDIT the IPv6 half (2026-09-13 "in the
 * configs wan card there is no ipv6 config options", repeated 2026-09-14 and
 * made precise: "wan card on configs tab should have radio buttons for 2 ipv6
 * rows: IPv6, IPv6 PD"). The first version of those rows (2026-09-13) was
 * read-only ON PURPOSE — the review that day kept the choice in the one
 * dialog that judges it against where it would land, so the card could not
 * become an unjudged editor. This module is how the card gets its radios
 * without becoming one: it builds exactly the wizard's sequence — the IPv6
 * record on the VLAN row (PATCH `…/vlans/:row`, gated server-side by the
 * shared `ipv6Refusals`), then on a ZTE chassis the `omci-wan` call that
 * delivers the CPE half over TR-069 (gated again before a line is composed)
 * — and nothing else. No `/parameters` write ever carries an IPv6 leaf.
 *
 * Two ASKS ride the one sequence:
 *
 *   wan   the wizard — VLAN, mode, addressing, firewall and IPv6 together.
 *         The row PATCH carries the mode (the dialog's own question) and the
 *         addressing where it changed (`addressingPatch`), so the route pushes
 *         the ONU's service config to the chassis as it always has.
 *   ipv6  the card — the IPv6 half ALONE, on the row as it is. The PATCH
 *         carries ONLY `ipv6`: the route treats that as the record-only
 *         write and pushes nothing (the record has no chassis half; the CPE
 *         half comes from `omci-wan`), so a prefix-mode change does not
 *         re-state every flow and GEM binding of a live subscriber. The
 *         omci-wan body is the row's own IPv4 line re-stated (a ZTE chassis
 *         re-renders `wan-ip` on every call; there is no IPv6-only form) and
 *         NO firewall: the route leaves the firewall alone when the field is
 *         absent, and the card did not ask about it.
 *
 * On a Huawei chassis neither ask calls omci-wan — the wizard never did (no
 * `wan-ip` verb there) — so the intent is a record, delivered by the
 * first-link push; the done text says so rather than claiming a CPE write.
 *
 * A THIRD ask, the card's firewall (operator, 2026-09-14: "the wan card
 * should also have a firewall radio button"):
 *
 *   firewall  the card — the firewall ALONE. No row PATCH, no omci-wan
 *             call: the IPv4 and IPv6 halves are not touched. Runs on the
 *             standalone `/firewall` verb (`plan.firewall`), which is
 *             CPE-first on every family — the CPE's own control over TR-069
 *             where the model has one, else the chassis verb on a ZTE.
 *
 * And when the card's `ipv6` ask carries `firewalled` too (a radio on each
 * row moved), the firewall rides `plan.firewall` on that SAME verb, for a
 * ROUTED row as much as a bridged one, and runs LAST — after the row and
 * the chassis call have landed. The wizard folds a routed row's firewall
 * into the ZTE `wan-ip` line instead; the card does not, on purpose: the
 * verb is CPE-first on every family (the wan-ip fold is chassis-only and
 * answers `unknown ME` on a linked Huawei ONT where the verb would have
 * written `X_HW_FirewallLevel`), and the card must never re-state the
 * wan-ip line — a subscriber drop — for a firewall click.
 *
 * WHICH writer the omci-wan call lands on is the SERVER's decision, read off
 * the linked CPE's document (the 2026-09-14 duplicate-WAN incident:
 * `wan-ip 1 mode dhcp` on RTEGC6A2955E, whose WAN is the CPE's own
 * TR-069-made `Internet_DHCP`, stood a second routed WAN up beside it). The
 * sentences here used to assume the chassis `wan-ip` line for every ZTE
 * unit; they now take the `writer` the dry run answers (`WanResult.writer`,
 * probed with `wanWriterProbeBody`) and hedge while it is unread, rather
 * than state a chassis line the unit may not have.
 */

/**
 * A FOURTH ask, the WAN card's one Apply (operator, 2026-10-07: "when you
 * apply settings even if they are identical with existing, delete and
 * reapply"; "set the wan with the option of omci (olt only) or tr069"):
 *
 *   card  the whole subscriber WAN at once — addressing, VLAN, particulars,
 *         IPv6 (only when the router manages it) — through ONE writer, the
 *         owner the card names, `rebuild: true`: deleted and built again,
 *         identical or not. The row is recorded first (its addressing, the
 *         IPv6 half); the omci-wan call carries `owner` and `rebuild` on EVERY
 *         vendor (the server sends a Huawei ONT to the router, a Huawei OLT's
 *         own WAN to its `ont ipconfig` form); the firewall verb runs last
 *         when its radio moved. The owner is recorded by the server once the
 *         WAN lands, never before — a refused apply leaves the record as it was.
 */
export type WanApplyAsk = 'wan' | 'ipv6' | 'firewall' | 'card';

/**
 * The IPv4 object the omci-wan call re-states on a ZTE chassis, in the
 * writer's own words — what every sentence below names:
 *
 *   zte_chassis            the chassis wan-ip line (the OMCI-made WAN, or an
 *                          unlinked unit)
 *   zte_tr069 / huawei_ont the CPE's own WAN connection, over TR-069 (the
 *                          connection the CPE terminates itself)
 *   null                   not read yet — the IPv4 WAN, whichever of the two
 *                          terminates it
 */
export function wanRestatedObject(writer: SubscriberWanWriter | null | undefined): string {
  if (writer === 'zte_chassis') return 'the chassis wan-ip line';
  if (writer === 'zte_tr069' || writer === 'huawei_ont') return "the CPE's own WAN connection (over TR-069)";
  return 'the IPv4 WAN (the chassis wan-ip line, or the CPE’s own connection over TR-069 — whichever terminates it on this unit)';
}

/**
 * The dry-run body that asks the server WHICH writer this unit's WAN takes
 * — addressing-free on purpose. The writer is decided by the linked CPE's
 * document and the VLAN alone (never by DHCP-versus-PPPoE), while the
 * chassis renderer refuses a PPPoE line without its password and a static
 * line without its address: a probe that carried the row's addressing would
 * 400 on exactly the rows the card most needs the writer for (a PPPoE row
 * whose password is stored and never echoed). No IPv6 intent rides it
 * either — the gate could refuse the pair, and the card already greys those
 * radios with the gate's own sentence. Nothing is written by a dry run.
 *
 * The ONE thing besides the document that moves the writer is a stand-down:
 * a routed row going bridged sends `mode: none`, and a linked unit with no
 * CPE connection on the VLAN then stays on the chassis (`no wan-ip` — there
 * is nothing to re-role) where a set would go over TR-069 (created). So the
 * dialog's Review probes the ask it will send: `standDown` for that case,
 * the routed set otherwise (the card's IPv6 ask is always a set).
 */
export function wanWriterProbeBody(
  choice: Pick<WanApplyChoice, 'vlanId'> & { standDown?: boolean },
): Record<string, unknown> {
  return choice.standDown
    ? { dryRun: true, mode: 'none', vlanId: choice.vlanId, connectionType: 'bridged' }
    : { dryRun: true, mode: 'dhcp', vlanId: choice.vlanId, connectionType: 'routed' };
}

export interface WanApplyChoice {
  ask: WanApplyAsk;
  /** Whether the chassis is a ZTE — the only vendor the omci-wan call is made on. */
  zte: boolean;
  /**
   * Whether a router is linked on the ACS. A BRIDGE is then sent to the
   * router too (2026-10-14): the server builds it where the router has none
   * (a Huawei ONT) and takes the chassis wan-ip off a ZTE ONT, read first.
   */
  routerLinked?: boolean;
  vlanId: number;
  /** The row this VLAN has on the ONU (PATCHed), or null = attached by this apply (POST). */
  row: Pick<OnuVlan, 'id' | 'mode' | 'addressing' | 'hasPppoePassword'> | null;
  mode: VlanMode;
  /** The addressing the routed line carries; ignored for a bridge. */
  addressing: VlanAddressing;
  /** Whether the operator typed any particular — what turns "kept as recorded" into a write on the row. */
  particularsTyped: boolean;
  pppoeUsername: string;
  pppoePassword: string;
  staticIp: string;
  staticMask: string;
  staticGateway: string;
  staticDns: string;
  /** The IPv6 intent, or null = nothing to carry (the half is untouched). */
  ipv6: WanIpv6 | null;
  /**
   * The firewall, or undefined = not asked. The wizard's ask: on a ZTE only
   * (folded into the wan-ip line, or the bridge's verb). The card's asks: on
   * every family, always the standalone verb (see the module comment).
   */
  firewalled: boolean | undefined;
  /** The card's ask: who manages the WAN — the OLT (omci) or the router (tr069). */
  owner?: WanOwner | undefined;
}

export interface WanApplyPlan {
  /** The VLAN row write, or null when this apply touches no row (the card's firewall alone). */
  row:
    | { kind: 'patch'; id: string; body: Record<string, unknown> }
    | { kind: 'attach'; body: Record<string, unknown> }
    | null;
  /** The omci-wan body, or null when this apply makes no chassis call. */
  wan: Record<string, unknown> | null;
  /** The standalone `/firewall` verb (a ZTE bridge from the wizard; every row from the card), or null. */
  firewall: { enabled: boolean; dryRun: false } | null;
}

/**
 * What the card says under its Firewall row the moment a radio moves — the
 * mode row's convention (operator, 2026-09-14). Plain, not `text-warn`: the
 * verb writes the CPE's own control over TR-069 or the chassis's firewall
 * line, and neither renegotiates the subscriber's WAN.
 */
export const WAN_FIREWALL_DISCLOSURE =
  'Applying writes the firewall on the CPE over TR-069, or on the chassis where the CPE has no control — after the IPv4 and IPv6 halves, in the tasks tray.';

/**
 * Where the card's Firewall row OPENS, said beside it — the shared
 * `defaultFirewall` order (the CPE's own reading, else the platform's
 * `onus.firewalled`, else assumed from the mode), in the row's own words:
 * "CPE reads: on" / "recorded: off" / "assumed on for a routed WAN". The
 * guess is said out loud, as the wizard's Review says it, because a radio
 * pre-selected on nothing must not read as a state anybody reported.
 */
/**
 * The firewall pill on a WAN card's title bar (operator, 2026-10-09: "show
 * firewall pill too for routed wan and mngt"), in the ONU card's words:
 * "Firewall on" / "Firewall off". It shows what someone REPORTED — the CPE's
 * own reading, else the platform's record. It never shows the guess that
 * `defaultFirewall` falls back to, because a title pill reads as a state.
 * Null means no pill.
 */
export function firewallTitlePill(
  cpe: boolean | null | undefined,
  recorded: boolean | null | undefined,
): { on: boolean; label: string; tone: 'success' | 'danger'; title: string } | null {
  const reading = typeof cpe === 'boolean' ? { value: cpe, from: 'as the router reports it' } : typeof recorded === 'boolean' ? { value: recorded, from: 'as recorded — the router reports no firewall of its own' } : null;
  if (!reading) return null;
  // Drawn as a shield alone — green on, red off (operator, 2026-10-28: "just a
  // pill with the shield icon") — so the words live in the tooltip.
  const label = reading.value ? 'Firewall on' : 'Firewall off';
  return {
    on: reading.value,
    label,
    tone: reading.value ? 'success' : 'danger',
    title: `${label} — the router's firewall, ${reading.from}. It guards the router as a whole, not one connection.`,
  };
}

export function firewallSourceLabel(input: { value: boolean; source: 'cpe' | 'recorded' | 'assumed' }, mode: VlanMode | null): string {
  const word = input.value ? 'on' : 'off';
  if (input.source === 'cpe') return `CPE reads: ${word}`;
  if (input.source === 'recorded') return `recorded: ${word}`;
  return `assumed ${word} for a ${mode === 'bridge' ? 'bridged' : 'routed'} WAN`;
}

/** The particulars the addressing implies, exactly as the wizard sends them on the row. */
function rowParticulars(choice: WanApplyChoice): Record<string, string> {
  if (choice.mode !== 'route') return {};
  if (choice.addressing === 'pppoe') {
    return { pppoeUsername: choice.pppoeUsername.trim(), pppoePassword: choice.pppoePassword };
  }
  if (choice.addressing === 'static') {
    return { staticIp: choice.staticIp, staticMask: choice.staticMask, staticGateway: choice.staticGateway };
  }
  return {};
}

export function wanApplyPlan(choice: WanApplyChoice): WanApplyPlan {
  /**
   * The card's firewall ALONE (operator, 2026-09-14: "the wan card should
   * also have a firewall radio button"): no row, no chassis call — the IPv4
   * and IPv6 halves are not touched — only the standalone verb, which is
   * CPE-first on every family. `firewalled` undefined here is a caller's
   * mistake (the card never builds this ask without a moved radio), and it
   * is answered with an empty plan rather than a verb carrying a guess.
   */
  if (choice.ask === 'firewall') {
    return {
      row: null,
      wan: null,
      firewall: choice.firewalled === undefined ? null : { enabled: choice.firewalled, dryRun: false },
    };
  }
  if (choice.ask === 'card') return cardPlan(choice);
  const routed = choice.mode === 'route';
  const addressing: VlanAddressing = routed ? choice.addressing : 'dhcp';
  const particulars = rowParticulars(choice);
  /*
    A password nobody typed is never sent — not on the row, where an empty one
    means "clear it", and not to the WAN, where it reached the router as an
    empty login (Monica Nekesa, HWTCF4F40D12, 2026-10-08: the wizard cleared
    the stored password at 11:21 and the router dialled 798 with none). Left
    out, the server takes the password it stores for the account.
  */
  if (particulars.pppoePassword === '') delete particulars.pppoePassword;
  /**
   * The IPv6 RECORD rides the row whenever this apply carries an intent.
   * Absent (untouched) where there is none — a target that refuses every
   * intent, or a read that failed — so a row that recorded something on such
   * a unit keeps it rather than being dropped by a caller that could not
   * judge. A bridged row carries none: the server clears it and refuses one.
   */
  const ipv6Record = choice.mode === 'bridge' || !choice.ipv6 ? {} : { ipv6: choice.ipv6 };
  /** The row's own addressing where it is routed — a bridged row's `dhcp` is a placeholder, not a record. */
  const recordedAddressing: VlanAddressing | null = choice.row?.mode === 'route' ? choice.row.addressing : null;

  const row: WanApplyPlan['row'] = choice.row
    ? {
        kind: 'patch',
        id: choice.row.id,
        body:
          choice.ask === 'wan'
            ? {
                mode: choice.mode,
                // Unchanged, it rides as NOTHING: the route reads an empty
                // `pppoePassword` as "clear it", so re-sending the addressing
                // with the untyped password would wipe the stored one.
                ...addressingPatch({
                  addressing,
                  recorded: routed ? recordedAddressing : null,
                  particularsTyped: choice.particularsTyped,
                  particulars,
                }),
                ...ipv6Record,
              }
            : // The IPv6 half alone — the route's record-only PATCH.
              { ...ipv6Record },
      }
    : {
        // Not on this ONU yet: attach it carrying the chosen shape. The
        // server re-checks the chassis's classification and refuses a
        // management VLAN whatever the caller offered.
        kind: 'attach',
        body: { vlanId: choice.vlanId, purpose: 'internet', mode: choice.mode, addressing, ...particulars, ...ipv6Record },
      };

  let wan: WanApplyPlan['wan'] = null;
  let firewall: WanApplyPlan['firewall'] = null;
  /**
   * The card's firewall rides the standalone verb on EVERY row and EVERY
   * family — never the wan-ip line (see the module comment: the verb is
   * CPE-first everywhere, the fold is chassis-only, and the card must not
   * re-state the line for a firewall click). It is planned before the
   * chassis branch below so a Huawei chassis, which makes no chassis call,
   * still gets it.
   */
  if (choice.ask === 'ipv6' && choice.firewalled !== undefined) {
    firewall = { enabled: choice.firewalled, dryRun: false };
  }
  // A CHOSEN owner sends the WAN call on every vendor (2026-10-07): the server
  // takes a Huawei ONT to the router and a Huawei OLT's own WAN to its form.
  if (choice.zte || (choice.owner && routed)) {
    if (routed) {
      wan = {
        mode: addressing,
        vlanId: choice.vlanId,
        // The wizard deletes and builds again, identical or not (operator, 2026-10-07).
        ...(choice.ask === 'wan' ? { rebuild: true } : {}),
        ...(choice.owner ? { owner: choice.owner } : {}),
        // The role, said outright. On a Huawei ONT this route goes to the
        // CPE over TR-069, and an EXISTING connection used to keep whatever
        // role it had — a swapped-in spare that arrived bridged on VLAN 5
        // stayed a bridge through "routed DHCP" and the LAN got no lease.
        // A ZTE wan-ip has no such leaf and ignores it.
        connectionType: 'routed',
        // Folded into the same transcript so a WAN that landed and a
        // firewall that did not cannot happen — the WIZARD's ask only.
        // Absent = the route leaves the firewall alone: the card's ask
        // never folds it here, its firewall is on the verb above.
        ...(choice.ask === 'wan' && choice.firewalled !== undefined ? { firewalled: choice.firewalled } : {}),
        // The IPv6 half never rides the wan-ip line: the route sends it to
        // the CPE over TR-069 (alone, on a ZTE ONU; beside the IPv4 half on
        // a Huawei ONT). Absent = not one IPv6 leaf is written.
        ...(choice.ipv6 ? { ipv6: choice.ipv6 } : {}),
        ...(addressing === 'pppoe'
          ? { username: choice.pppoeUsername.trim(), ...(choice.pppoePassword ? { password: choice.pppoePassword } : {}) }
          : {}),
        ...(addressing === 'static'
          ? {
              staticIp: choice.staticIp,
              staticMask: choice.staticMask,
              staticGateway: choice.staticGateway,
              ...(choice.staticDns ? { staticDns: choice.staticDns } : {}),
            }
          : {}),
      };
    } else {
      if (choice.row?.mode === 'route' || choice.routerLinked) {
        // ZTE ONU: removes the OLT wan-ip (read first; nothing when there is
        // none). Huawei ONT: the route re-roles the CPE's existing connection
        // to a bridge, or BUILDS one where the router has none (2026-10-14:
        // "VLAN 46 bridged" on HS MC6 - Maggie3 used to send nothing to the
        // router, so the record said bridge and the router had no bridge).
        wan = { mode: 'none', vlanId: choice.vlanId, connectionType: 'bridged' };
      }
      // Bridge, or the stand-down above (mode 'none' carries no firewall
      // token): the wizard's firewall goes on its own verb when it was
      // asked (the card's is already there, whatever the row).
      if (choice.firewalled !== undefined) firewall = { enabled: choice.firewalled, dryRun: false };
    }
  }
  return { row, wan, firewall };
}

/**
 * The WAN card's ONE Apply as calls (see the `card` ask): the row, then the
 * WAN through the chosen owner with `rebuild`, then the firewall verb.
 */
function cardPlan(choice: WanApplyChoice): WanApplyPlan {
  const particulars = rowParticulars(choice);
  // An empty password on the row means "clear it": one not typed is never sent,
  // so the account the platform stores stays stored.
  if (particulars.pppoePassword === '') delete particulars.pppoePassword;
  const recordedAddressing: VlanAddressing | null = choice.row?.mode === 'route' ? choice.row.addressing : null;
  // IPv6 is a router leaf: an OLT-managed WAN carries none, and the row keeps what it recorded.
  const ipv6 = choice.owner === 'omci' ? null : choice.ipv6;
  const ipv6Record = ipv6 ? { ipv6 } : {};
  const row: WanApplyPlan['row'] = choice.row
    ? {
        kind: 'patch',
        id: choice.row.id,
        body: {
          mode: 'route',
          ...addressingPatch({
            addressing: choice.addressing,
            recorded: recordedAddressing,
            particularsTyped: choice.particularsTyped,
            particulars,
          }),
          ...ipv6Record,
        },
      }
    : {
        kind: 'attach',
        body: { vlanId: choice.vlanId, purpose: 'internet', mode: 'route', addressing: choice.addressing, ...particulars, ...ipv6Record },
      };
  const wan: Record<string, unknown> = {
    mode: choice.addressing,
    vlanId: choice.vlanId,
    connectionType: 'routed',
    rebuild: true,
    ...(choice.owner ? { owner: choice.owner } : {}),
    ...(ipv6 ? { ipv6 } : {}),
    ...(choice.addressing === 'pppoe'
      ? {
          username: choice.pppoeUsername.trim(),
          // Left out when not typed: the server takes the password it stores for this VLAN.
          ...(choice.pppoePassword ? { password: choice.pppoePassword } : {}),
        }
      : {}),
    ...(choice.addressing === 'static'
      ? {
          staticIp: choice.staticIp,
          staticMask: choice.staticMask,
          staticGateway: choice.staticGateway,
          ...(choice.staticDns ? { staticDns: choice.staticDns } : {}),
        }
      : {}),
  };
  return {
    row,
    wan,
    firewall: choice.firewalled === undefined ? null : { enabled: choice.firewalled, dryRun: false },
  };
}

/**
 * The PPPoE password the WAN card's Apply sends. The field opens on the one
 * kept for the router's connection, and sending that back on every Apply is
 * how a stale one went onto the router again after the WAN dialog had set a
 * new one (Monica Nekesa, HWTCF4F40D12, 2026-10-08). So only a TYPED password
 * is sent; untouched, it is left out and the server applies the one the record
 * holds for this VLAN — unless the record holds none, when the field's is the
 * only one there is.
 */
export function cardPasswordToSend(input: { shown: string; opened: string; recorded: boolean }): string {
  if (input.shown !== input.opened) return input.shown;
  return input.recorded ? '' : input.shown;
}

/** Who manages the WAN, in the card's own words. */
export const WAN_OWNER_LABELS: Record<WanOwner, string> = { omci: 'OLT (OMCI)', tr069: 'Router (TR-069)' };

/**
 * The card's Apply, said before the click (it rebuilds: the subscriber drops).
 * Null when the card cannot apply — the reason is then the bar's `blocked`.
 */
export function cardApplyDisclosure(choice: Pick<WanApplyChoice, 'addressing' | 'vlanId' | 'owner'>): string {
  const who = choice.owner === 'omci' ? 'the OLT' : 'the router';
  return `Apply deletes this WAN and builds it again as ${ADDRESSING_WORD[choice.addressing]} on VLAN ${choice.vlanId}, managed by ${who} — even when nothing changed. The subscriber drops for a few seconds.`;
}

/**
 * What stops the IPv6-alone ask before a call is made, in a sentence the
 * card shows beside its Save — null when the plan can be sent. The wizard
 * validates its own steps (`addressingReady`, the gate on the IP protocol
 * step) and never reaches here refused; the card has no steps, so the row's
 * own state is judged when the operator's choice is about to ride it.
 *
 * A ZTE chassis re-states the IPv4 WAN on every omci-wan call — the `wan-ip`
 * line, or the CPE's own connection over TR-069, whichever writer the unit
 * takes — so a PPPoE row needs its account (the stored password stands in
 * server-side since 2026-09-19; one is typed only when none is stored) and a
 * static row needs the address, mask and gateway it recorded; a row that
 * recorded a static WAN without them cannot have its WAN re-stated from
 * here. The sentence names the object the writer re-states (`writer`, from
 * the dry run; hedged while unread).
 *
 * Only the ROW's own state is judged here. A row-less ONU and a target that
 * yields no intent never build a choice at all — the card greys its radios
 * for both and says why under them (review, 2026-09-14: the first version
 * let a row-less ONU's radios move and then held Apply with a sentence
 * about an unread target, while the by-name sentence sat in a branch no
 * caller could reach).
 */
export function wanApplyReadiness(choice: WanApplyChoice, writer: SubscriberWanWriter | null = null): string | null {
  if (choice.ask !== 'ipv6' || !choice.zte) return null;
  const object = wanRestatedObject(writer);
  if (choice.addressing === 'pppoe') {
    if (!choice.pppoeUsername.trim()) {
      return `The row records PPPoE without a username — the apply re-states ${object} from it; set the account in the WAN mode wizard (Edit) first.`;
    }
    // A stored password stands in server-side (2026-09-19); only a row
    // that holds none needs one typed.
    if (!choice.pppoePassword && !choice.row?.hasPppoePassword) {
      return `The row is PPPoE and the apply re-states ${object} with the password, and none is stored for this account — type it to apply.`;
    }
  }
  if (choice.addressing === 'static' && (!choice.staticIp || !choice.staticMask || !choice.staticGateway)) {
    return `The row records a static WAN without its address, mask and gateway — the apply re-states ${object} from them; set them in the WAN mode wizard (Edit) first.`;
  }
  return null;
}

/** The addressing as the WAN card's own mode row spells it. */
const ADDRESSING_WORD: Record<VlanAddressing | WanMode, string> = { dhcp: 'DHCP', static: 'Static', pppoe: 'PPPoE', unknown: 'Unknown' };

/** The IPv4 line the card's ask re-states on a ZTE chassis, in the card's own words ("DHCP on VLAN 5"). */
export function wanApplyRestatedLine(choice: WanApplyChoice): string {
  return `${ADDRESSING_WORD[choice.addressing]} on VLAN ${choice.vlanId}`;
}

/**
 * What the CPE's IPv4 WAN is running, as the WAN card knows it — the device
 * document's own reading of the connection, with the card's pending draft
 * beside it. The pieces the re-stated `wan-ip` line would put back.
 */
export interface CpeIpv4Wan {
  /** Whether the card's IPv4 half has an edit in flight or unsent (a mode change, a VLAN or account write). */
  editPending: boolean;
  mode: WanMode;
  vlanId: string | null;
  pppoeUsername: string | null;
}

/**
 * What stops the card's ask when the VLAN ROW and the CPE disagree about the
 * IPv4 WAN — the review of 2026-09-14's high finding.
 *
 * The card's ask re-states the IPv4 WAN FROM THE ROW (there is no IPv6-only
 * form of omci-wan) — the chassis `wan-ip` line on the OMCI-made shape, the
 * CPE's own connection over TR-069 where the CPE terminates it — and the row
 * is not the only writer of the CPE's IPv4 WAN: the card's own Addressing
 * row rebuilds the connection over TR-069 (`POST /devices/:id/wan`) and never
 * touches `onu_vlans`. So an F670L whose row records DHCP and whose CPE was
 * switched to PPPoE from this card would, on an IPv6 apply, be put back on
 * DHCP and lose its PPPoE WAN — with the bar having said the IPv6 half was
 * sent. The wizard has the same pass-through but shows the row's addressing
 * on its own step and on Review; the card shows only the CPE's, so it must
 * hold by name instead. The rule holds on BOTH writers; only the object the
 * sentence names changes (`writer`, from the dry run; hedged while unread).
 *
 * Judged on a ZTE chassis for a routed row, where the CPE's word is known:
 *
 *   - an IPv4 edit pending on the card (drafted, in flight, or landed but not
 *     re-read yet — `modeChanged` / `writes` stay true until the device
 *     document re-seeds the form), because the re-stated WAN would carry
 *     the row's OLD mode / VLAN / account over the change just sent;
 *   - the CPE's mode, VLAN or PPPoE account differing from the row's — the
 *     same write, the same override, from drift that landed earlier.
 *
 * The VLAN and the account are judged beside the mode because the re-stated
 * WAN carries all three (`subscriberWanRow` falls back to the FIRST
 * subscriber row when the platform holds none on the CPE's WAN VLAN, and a
 * PPPoE WAN dials as the row's account). The CPE's word can lag a write it
 * has accepted and not run yet — that window is not closed from here.
 */
export function wanApplyConflict(
  choice: WanApplyChoice,
  cpe: CpeIpv4Wan,
  writer: SubscriberWanWriter | null = null,
): string | null {
  if (choice.ask !== 'ipv6' || !choice.zte || choice.mode !== 'route') return null;
  const line = wanApplyRestatedLine(choice);
  const object = wanRestatedObject(writer);
  if (cpe.editPending) {
    return `Apply the IPv4 change on its own first — on this chassis the IPv6 save re-states ${object} from the row (${line}), which would override the change just sent to the CPE.`;
  }
  if (cpe.mode !== 'unknown' && cpe.mode !== choice.addressing) {
    return `The row records ${ADDRESSING_WORD[choice.addressing]} while the CPE runs ${ADDRESSING_WORD[cpe.mode]} — on this chassis ${object} is re-stated from the row, which would put the CPE back on ${line}; set the addressing in the WAN mode wizard (Edit) first.`;
  }
  if (cpe.vlanId && cpe.vlanId !== String(choice.vlanId)) {
    return `The row is VLAN ${choice.vlanId} while the CPE runs its WAN on VLAN ${cpe.vlanId} — on this chassis ${object} is re-stated from the row, which would move the WAN; attach VLAN ${cpe.vlanId} on the VLANs tab or set the VLAN in the WAN mode wizard (Edit) first.`;
  }
  if (choice.addressing === 'pppoe' && cpe.pppoeUsername && cpe.pppoeUsername !== choice.pppoeUsername.trim()) {
    return `The row records the PPPoE account ${choice.pppoeUsername.trim()} while the CPE dials as ${cpe.pppoeUsername} — on this chassis ${object} is re-stated from the row; set the account in the WAN mode wizard (Edit) first.`;
  }
  return null;
}

/**
 * What the card's Apply DOES beyond the IPv6 half, said before the click the
 * way the card's mode row says "Applying will rebuild the WAN as … The
 * subscriber drops briefly" the moment its radio moves (review, 2026-09-14:
 * the first version's bar read "Sent to the device immediately" over an
 * apply that renegotiates the subscriber's WAN on a ZTE chassis and sends
 * nothing to the CPE on a Huawei one). `warn` is the mode row's tone — a
 * service blip; a record-only write is plain. Null for the wizard's ask,
 * whose Review says all of this itself.
 *
 * WHICH object is re-stated is read off the dry run (`writer`), never
 * assumed: the sentence used to say "the chassis wan-ip line" for every ZTE
 * unit, and on the 24 units whose WAN is the CPE's own `Internet_DHCP` that
 * line is exactly the 2026-09-14 duplicate. Unread, it names both and says
 * the server decides.
 */
export function wanApplyDisclosure(
  choice: WanApplyChoice,
  writer: SubscriberWanWriter | null = null,
): { warn: boolean; text: string } | null {
  if (choice.ask !== 'ipv6') return null;
  if (!choice.zte) {
    return {
      warn: false,
      text: 'Applying records the choice on the VLAN row only — a Huawei chassis writes no CPE half from here; it is delivered by the first-link push when the CPE next links or relinks.',
    };
  }
  if (choice.mode !== 'route') return null;
  const line = wanApplyRestatedLine(choice);
  if (writer === 'zte_chassis') {
    return {
      warn: true,
      text: `Applying re-states the chassis wan-ip line as ${line} with the IPv6 change (there is no IPv6-only form) — the subscriber drops briefly while it comes back up.`,
    };
  }
  if (writer === 'zte_tr069' || writer === 'huawei_ont') {
    return {
      warn: true,
      text: `Applying re-states the CPE's own WAN connection as ${line} over TR-069 with the IPv6 change (there is no IPv6-only form; this unit's WAN is the CPE's, so no chassis wan-ip line is sent) — the subscriber drops briefly while it comes back up.`,
    };
  }
  return {
    warn: true,
    text: `Applying re-states the IPv4 WAN as ${line} with the IPv6 change (there is no IPv6-only form) — on the chassis wan-ip line or the CPE's own connection over TR-069, whichever terminates this unit's WAN; the server reads the CPE and decides. The subscriber drops briefly while it comes back up.`,
  };
}

/** What the write routes report back about their own push to the chassis. */
export type PushResult = { executed: boolean; errors: string[]; warnings: string[] } | null;

/** What /omci-wan answers — the chassis's own transcript shape, plus which writer took the WAN and why. */
export interface WanResult {
  executed: boolean;
  warnings: string[];
  transcript: string | null;
  writer?: SubscriberWanWriter;
  writerReason?: string;
}

/** The attach route's word on the router's own bridge for a bridged VLAN beside the subscriber WAN (2026-10-31). */
export type RouterBridgeNote = { status: 'present' | 'created' | 'unread' | 'skipped' | 'failed'; note: string };

export interface WanApplyResult {
  saved: { applied: PushResult; router?: RouterBridgeNote | null };
  wan: WanResult | null;
  /** The firewall half's own outcome when it did not land — a note beside the landed VLAN, never the apply's fate. */
  firewallNote: string | null;
}

/** The tray row's label — what the operator asked for, named so a toast makes sense out of context. */
export function wanApplyLabel(choice: WanApplyChoice, serial: string): string {
  if (choice.ask === 'firewall') return `WAN firewall — ${serial}`;
  if (choice.ask === 'card') return `WAN rebuild — ${serial}`;
  return choice.ask === 'ipv6' ? `WAN IPv6 — ${serial}` : `WAN mode — ${serial}`;
}

/**
 * The card's firewall, as the toast says it once the verb has run: what was
 * asked and where it went is the verb's own business (the CPE over TR-069
 * where it has a control, else the chassis), so the sentence names the
 * state and lets the note — a queued write, a model without the ME, a
 * Huawei chassis with nothing to write — say the rest.
 */
function firewallDoneSentence(enabled: boolean, note: string | null): string {
  return note ?? `The firewall is now ${enabled ? 'on' : 'off'}.`;
}

/**
 * The done text — what the toast says once the sequence settles, built from
 * the choice because the surface that started it is gone by then.
 *
 * Plain, by the operator's order (2026-09-19: "remove all that extra olt
 * jargon … and job toast"): what was asked, whether it landed — applied,
 * queued for the ONU's next check-in, or refused — and one short clause for
 * a half that did not (IPv6, firewall). Never which writer took the WAN,
 * never a parameter list; the ONU page holds the detail for whoever wants it.
 */
export function wanApplyDoneText(choice: WanApplyChoice, result: WanApplyResult, serial: string): string {
  const { saved, wan, firewallNote } = result;
  if (choice.ask === 'firewall') {
    return `${serial}: ${firewallDoneSentence(choice.firewalled ?? false, firewallNote)}`;
  }
  const v6 = choice.mode === 'bridge' || !choice.ipv6 ? '' : `, IPv6 ${describeWanIpv6(choice.ipv6)}`;
  const what =
    choice.ask === 'ipv6'
      ? `IPv6 ${describeWanIpv6(choice.ipv6)}`
      : choice.mode === 'bridge'
        ? 'bridged'
        : `routed ${ADDRESSING_WORD[choice.addressing]}${v6}`;
  const head =
    choice.ask === 'card'
      ? `${serial}: WAN rebuilt as ${what} on VLAN ${choice.vlanId}, managed by ${choice.owner === 'omci' ? 'the OLT' : 'the router'}`
      : `${serial}: VLAN ${choice.vlanId} ${what}`;
  const cardFirewall =
    choice.ask === 'ipv6' && choice.firewalled !== undefined ? ` ${firewallDoneSentence(choice.firewalled, firewallNote)}` : '';
  // The router's own bridge for a VLAN beside the subscriber WAN: said when it was built or could not be, never when it was already there.
  const routerBridge = saved.router && saved.router.status !== 'present' ? ` ${saved.router.note}` : '';
  const tail = `${ipv6Outcome(wan?.warnings ?? [], serial)}${!cardFirewall && firewallNote ? ` ${firewallNote}` : ''}${cardFirewall}${routerBridge}`;
  if (choice.ask === 'ipv6' && !choice.zte) return `${head} — recorded; it reaches the ONU when it next links.${tail}`;
  if (saved.applied === null && choice.ask !== 'ipv6') {
    return `${head} — recorded; the OLT has no CLI credentials, so it reaches the ONU when it can be written.${tail}`;
  }
  const refusal = wanApplyRefusal(saved.applied);
  if (refusal) return `${head} — ${refusal}${tail}`;
  // The WAN went to the CPE over TR-069 and the CPE did not answer: it lands at its next check-in.
  const queued = wan !== null && wan.executed === false;
  return `${head} — ${queued ? 'queued; it lands at the ONU’s next check-in' : 'applied'}.${tail}`;
}

/**
 * What the OLT said when it did not take the VLAN, as one sentence, or null
 * when it took it. Read where the operator already is — the toast — because
 * the ONU page never showed it: "open X to read them" sent them to an
 * overview with nothing on it (HWTC37698B0E, 2026-10-14). The first refusal
 * is quoted with the OLT's own answer; the rest are counted. The record is
 * kept either way, and the sentence says so.
 */
export function wanApplyRefusal(applied: WanApplyResult['saved']['applied']): string | null {
  if (!applied || applied.errors.length === 0) return null;
  const [first, ...rest] = applied.errors;
  const more = rest.length > 0 ? ` (and ${rest.length} more)` : '';
  return applied.executed
    ? `not applied: the OLT refused ${first}${more}. The record is kept; Resync sends it again.`
    : `not sent to the OLT: ${first}${more}. The record is kept; Resync sends it again.`;
}

/** The IPv6 half's outcome as one short clause, or nothing when it landed (or was not asked). */
function ipv6Outcome(warnings: string[], serial: string): string {
  const note = warnings.find((w) => /IPv6/.test(w));
  if (!note) return '';
  if (/PARTLY|partial/i.test(note)) return ' IPv6 partly applied — apply again once the ONU has checked in.';
  if (/queued/i.test(note)) return ' IPv6 lands at the ONU’s next check-in.';
  if (/not applied|not written|nothing was written|faulted|refused|skipped/i.test(note)) return ` IPv6 was not applied — open ${serial} for the reason.`;
  return '';
}

/**
 * The calls, in order, against an API client — the one sequence both
 * surfaces hand to the tasks tray. `api` is injected so the sequence is
 * pinned without a network: the row first (PATCH or attach), then the
 * chassis (omci-wan) where the plan has one, then the firewall verb (the
 * wizard's bridged one; the card's on any row) — always LAST.
 *
 * The firewall half's outcome is reported BESIDE the landed VLAN, never as a
 * failure of the whole apply — the way omci-wan reports a failed IPv6 half
 * beside a landed IPv4 half. The standalone `/firewall` verb answers 400 on
 * an ONU model without the OMCI firewall ME (an HG8546M on OMCI), and it is
 * the LAST call: by then the row and its chassis push have landed. Letting
 * that 400 fail the task read "Nothing was changed" about a VLAN that had —
 * and the tray skips `invalidate` on a failure. On the routed path the
 * chassis's own `unknown ME` answer for the folded firewall line is read off
 * the omci-wan warnings (`firewallModelNote`) and named the same way.
 *
 * The card's firewall-alone ask has no row and no chassis call: the verb is
 * the whole sequence, and its refusal is then the whole outcome — still a
 * note, so the toast says what the verb answered rather than "Nothing was
 * changed" about nothing. The verb's own warning (the CPE did not answer
 * the connection request; the write is queued for its next inform) is
 * read the same way, so a firewall that has not landed yet is never
 * announced as landed.
 */
export async function runWanApply(
  plan: WanApplyPlan,
  endpoints: { rowsBase: string; wan: string; firewall: string },
  api: {
    post: <T>(path: string, body?: unknown) => Promise<T>;
    patch: <T>(path: string, body?: unknown) => Promise<T>;
  },
): Promise<WanApplyResult> {
  const saved: WanApplyResult['saved'] =
    plan.row === null
      ? { applied: null }
      : plan.row.kind === 'patch'
        ? await api.patch<OnuVlan & { applied: PushResult }>(`${endpoints.rowsBase}/${encodeURIComponent(plan.row.id)}`, plan.row.body)
        : await api.post<OnuVlan & { applied: PushResult; router?: RouterBridgeNote | null }>(endpoints.rowsBase, plan.row.body);
  let wan: WanResult | null = null;
  let firewallNote: string | null = null;
  if (plan.wan) {
    wan = await api.post<WanResult>(endpoints.wan, plan.wan);
    // The route tolerates the model's `unknown ME` on the optional firewall
    // line (the WAN landed) but adds no sentence of its own — the chassis's
    // answer is in the warnings, and the tray names it.
    if (plan.wan.mode !== 'none') firewallNote = firewallModelNote(wan.warnings);
  }
  if (plan.firewall) {
    try {
      const answer = await api.post<{ warnings?: string[] } | null | undefined>(endpoints.firewall, plan.firewall);
      const queued = answer?.warnings?.find((w) => /queued|next inform/i.test(w));
      if (queued) firewallNote = queued;
    } catch (err) {
      firewallNote = `The firewall (${plan.firewall.enabled ? 'on' : 'off'}) was not written: ${
        err instanceof Error ? err.message : String(err)
      }`;
    }
  }
  return { saved, wan, firewallNote };
}
